import type { SecretListing } from "@repo/app-types/entities/secret";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { TableCard } from "../../../components/layout/pageLayout.js";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";

interface SecretTableProps {
  "aria-label": string;
  secrets: readonly SecretListing[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage: string;
  /** Shown after the secret's name, such as a warning. */
  renderAccessory?: (secret: SecretListing) => ReactNode;
  /** Buttons shown at the row's end on hover or focus. */
  renderActions: (secret: SecretListing) => ReactNode;
}

export const SecretTable: FC<SecretTableProps> = (props) => {
  const { renderAccessory, renderActions } = props;

  // Not memoized: callers pass new render functions on every render.
  const columns: EntityTableColumnDefinition<SecretListing>[] = [
    {
      id: "secret",
      header: "Secret",
      isRowHeader: true,
      getTextValue: ({ name }) => name,
      renderCell: (secret) => {
        const accessory = renderAccessory?.(secret);
        return (
          <div className="flex">
            {/* `w-0 flex-auto` keeps the nowrap text out of the table's width, so it truncates. */}
            <div className="w-0 flex-auto">
              <div className="flex items-center gap-1.5">
                <span className="truncate font-mono text-sm font-medium text-foreground">
                  {secret.name}
                </span>
                {accessory ? (
                  <span className="flex shrink-0">{accessory}</span>
                ) : null}
              </div>
              {/* A stored value can't be read back; the dots say one is there. */}
              <div
                className={twMerge(
                  "font-mono text-xs text-muted-foreground",
                  PH_MASK_CLASS,
                )}
              >
                ••••{secret.value_hint ?? ""}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "updatedAt",
      header: "Last updated",
      className: "w-0 whitespace-nowrap",
      renderCell: (secret) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {DateTime.fromISO(secret.updated_at).toLocaleString(
            DateTime.DATETIME_MED,
          )}
        </span>
      ),
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      className: "w-0",
      // Shown on hover or focus; `invisible` keeps their width, so nothing
      // shifts. `group-[:hover]`, as EntityTable's link rows use:
      // `group-hover` never matches in its rows.
      renderCell: (secret) => (
        <div className="invisible flex items-center gap-1 group-[:focus-within]/row:visible group-[:hover]/row:visible">
          {renderActions(secret)}
        </div>
      ),
    },
  ];

  return (
    <TableCard>
      <EntityTable
        aria-label={props["aria-label"]}
        columns={columns}
        entities={props.secrets}
        isLoading={props.isLoading}
        isError={props.isError}
        emptyMessage={props.emptyMessage}
        size={Size.SMALL}
        isFullBleed
      />
    </TableCard>
  );
};
